import React, { useMemo } from 'react';
import { Badge, theme } from 'antd';
import type { BadgeProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

export interface AndBadgeProps extends BadgeProps, BaseComponentProps, DataSetConfig {
  develop?: boolean;
}

export const AndBadge: React.FC<AndBadgeProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  count,
  showZero,
  overflowCount,
  dot,
  offset,
  size,
  status,
  text,
  color,
  title,
  children,
  ...restProps
}) => {
  const getDataSet = useAndStore((state) => state.getDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const _dataValue = useMemo(() => {
    if (dataSetKey && dataSetField) return getDataSet(dataSetKey, dataSetField);
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // 计算最终显示的 count (优先使用绑定数据)
  const effectiveCount = useMemo(() => {
    if (_dataValue !== undefined && _dataValue !== null) {
      // 尝试转为数字，如果失败则直接作为 ReactNode (但 Badge count 需要 ReactNode)
      return _dataValue;
    }
    return count;
  }, [_dataValue, count]);

  // Safely get token
  const tokenResult = theme.useToken ? theme.useToken() : { token: undefined };
  const antdToken = tokenResult.token;
  
  // Resolve color token
  const resolvedColor = useMemo(() => {
    if (!color || typeof color !== 'string') return undefined;
    if (antdToken && color in antdToken) {
       return (antdToken as any)[color];
    }
    return color;
  }, [color, antdToken]);

  // 如果没有 children，通常是 Status Badge 模式，不需要 slot 容器
  // 但为了 GrapesJS 拖拽体验，通常我们会给一个空容器让用户拖进去
  // 除非 text 属性存在，那是 Status Badge 模式
  
  // 我们总是渲染一个 Slot 容器，让 GrapesJS 把子元素放进去
  // 如果用户只填写 text 和 status，子元素为空，Antd Badge 会渲染为小红点在文本旁
  
  return (
    <Badge
      {...restProps}
      count={effectiveCount}
      showZero={showZero}
      overflowCount={overflowCount}
      dot={dot}
      offset={offset}
      size={size}
      status={status}
      text={text}
      color={resolvedColor}
      title={title}
    >
      {/* 
        GrapesJS 注入点
        注意: Badge 会包裹它的 children。
        我们需要一个稳定的容器来承载 GrapesJS 的 DOM 元素。
      */}
      <span 
        data-slot="content" 
        className="and-badge-content-slot"
        style={{ 
          display: 'inline-block',
          minWidth: (!children && !text) ? '20px' : undefined, // 给一个最小宽度以便拖拽？
          minHeight: (!children && !text) ? '20px' : undefined 
        }}
      >
        {children}
      </span>
    </Badge>
  );
};

export default AndBadge;
